import { useEffect } from 'react';
import { TOUCH_QUERY } from './useMediaQuery';

const FIELD_SELECTOR = 'input, textarea, select, [contenteditable="true"]';

/**
 * Publishes the visual viewport height as `--app-height` (consumed by `#root`
 * in index.scss) on touch devices.
 *
 * iOS Safari ignores `interactive-widget=resizes-content`: the on-screen
 * keyboard overlays the page instead of shrinking it, so a full-height app
 * shell keeps its composer hidden behind the keyboard. Sizing the shell to the
 * visual viewport keeps bottom-anchored UI above the keyboard.
 */
export function useVisualViewportHeight() {
  useEffect(() => {
    const vv = window.visualViewport;

    if (!vv || !window.matchMedia(TOUCH_QUERY).matches) {
      return undefined;
    }

    const root = document.documentElement;
    let frame = 0;

    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // Pinch-zoom also shrinks the visual viewport — don't resize the app for it.
        if (vv.scale > 1.01) {
          return;
        }

        root.style.setProperty('--app-height', `${Math.round(vv.height)}px`);

        /*
         * iOS scrolls the window to reveal a focused field even when the shell
         * already fits above the keyboard, leaving a gap and pushing the header
         * off-screen. Undo that — but only for fixed-height app pages (not ones
         * whose content outgrows #root and scrolls the document, like /projects).
         * Measure #root, not <html>: iOS keeps <html> at the full layout height
         * while the keyboard is open, so it never "fits".
         */
        const appRoot = document.getElementById('root');
        const fieldFocused = document.activeElement?.matches(FIELD_SELECTOR) ?? false;
        const shellFits = !!appRoot && appRoot.scrollHeight <= Math.ceil(vv.height) + 1;

        if (fieldFocused && shellFits && window.scrollY > 0) {
          window.scrollTo(0, 0);
        }
      });
    };

    update();
    vv.addEventListener('resize', update);

    return () => {
      cancelAnimationFrame(frame);
      vv.removeEventListener('resize', update);
      root.style.removeProperty('--app-height');
    };
  }, []);
}
